/* eslint-disable import/prefer-default-export */
import React, { useEffect, useRef, useState } from "react";
import {
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography
} from "@mui/material";
import { useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import { ErrorTemplate } from "../../../templates";
import LoadingComponent from "../../LoadingComponent";
import "../index.css";
import { fireGtagEvent } from "../../../utils/google-analytics";
import {
  emailRegex,
  gstNumberRegex,
  phoneNumberRegex,
  zipRegex
} from "../../../constants";
import { useAddAddressMutation } from "../../../hooks";

export function AddAddress(props) {
  const {
    mutate: addAddress,
    status: addStatus,
    data
  } = useAddAddressMutation();
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
  const navigate = useNavigate();

  const { details, shopifyDetails } = useSelector((state) => state.user);
  const states = JSON.parse(process.env.REACT_APP_STATES);
  const [formErrors, setFormErrors] = useState({});
  const formContainerRef = useRef();
  const setDefaults = React.useCallback(
    () => ({
      firstName: shopifyDetails?.firstName || details?.firstName,
      lastName: shopifyDetails?.lastName || details?.lastName,
      zip: "",
      email: shopifyDetails?.email || details?.email,
      phone: shopifyDetails?.phoneNumber || details?.phoneNumber,
      label: "",
      address1: "",
      address2: "",
      city: "",
      country: "India",
      province: "",
      gstEnabled: false,
      gstNumber: "",
      companyName: ""
    }),
    [shopifyDetails, details]
  );

  const [address, setAddressValues] = useState(setDefaults());
  const [inputOpen, setInputOpen] = useState(false);
  const switchInput = () => {
    setInputOpen(!inputOpen);
  };

  const validate = (values) => {
    const errors = {};
    if (!values.firstName) {
      errors.firstName = "First Name is required";
    }
    if (!values.lastName) {
      errors.lastName = "Last Name is required";
    }
    if (!values.email) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(values.email)) {
      errors.email = "Email is invalid!";
    }
    if (!phoneNumberRegex.test(values.phone)) {
      errors.phone = "Phone number is invalid!";
    }
    if (!zipRegex.test(values.zip)) {
      errors.zip = "Zip code is invalid!";
    }
    if (!values.address1) {
      errors.address1 = "Address is required";
    }
    if (!values.province) {
      errors.province = "State is required";
    }
    if (!values.city) {
      errors.city = "City is required";
    }

    if (values.gstEnabled) {
      if (!gstNumberRegex.test(values.gstNumber)) {
        errors.gstNumber = "Invalid GST Number provided!";
      }

      if (values.companyName.length < 3) {
        errors.companyName =
          "Company Name should be atleast 3 characters long.";
      }

      if (values.companyName.length > 160) {
        errors.companyName = "Company Name should be less than 160 characters.";
      }
    }

    setFormErrors(errors);
    return errors;
  };

  useEffect(() => {
    if (Object.keys(formErrors).length) {
      validate(address);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  const handleChange = (e) => {
    e.preventDefault();
    setAddressValues({ ...address, ...{ [e.target.name]: e.target.value } });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormErrors({});
    const errors = validate(address);

    if (Object.keys(errors).length === 0) {
      let companyName = null;
      let gstNumber = null;

      if (address.gstEnabled) {
        companyName = address.companyName;
        gstNumber = address.gstNumber;
      }

      const provinceCode = states.find(
        (state) => state.label === address.province
      ).value;

      addAddress({
        ...address,
        zip: parseInt(address.zip, 10),
        provinceCode,
        companyName,
        gstNumber
      });

      props?.onSubmitExtra();
      fireGtagEvent("address_added");
    }
  };

  if (addStatus === "pending") {
    return <LoadingComponent />;
  }

  if (data?.address?.id) {
    // TODO:: shipping or billing address add handling
    const shippingAddressId = data.address.id;
    if (isMobile) {
      navigate("/choose-type", { state: { shippingAddressId } });
    } else {
      navigate("/address", { state: { shippingAddressId } });
    }
  }

  const handleApplyGst = () => {
    if (formContainerRef.current) {
      formContainerRef.current.scrollTop = 500;
    }
    setAddressValues({
      ...address,
      gstEnabled: !address.gstEnabled
    });
  };

  return (
    <div className="add-new-address">
      <div className="new-address-heading">
        <h2>Add New Address</h2>
      </div>
      <form id="add_address_form" onSubmit={handleSubmit}>
        <div className="form-container" ref={formContainerRef}>
          <div className="name-email">
            <div className="two-divider">
              <TextField
                label="First Name"
                variant="outlined"
                className="name input-props"
                type="text"
                name="firstName"
                value={address.firstName}
                onChange={handleChange}
                error={!!formErrors.firstName}
              />
              <ErrorTemplate error={formErrors.firstName} />
            </div>
            <div className="two-divider">
              <TextField
                label="Last Name"
                variant="outlined"
                type="text"
                name="lastName"
                value={address.lastName}
                onChange={handleChange}
                className="email input-props"
                error={!!formErrors.lastName}
              />
              <ErrorTemplate error={formErrors.lastName} />
            </div>
          </div>
          <div className="name-email">
            <div className="two-divider">
              <TextField
                label="Email"
                variant="outlined"
                type="text"
                name="email"
                value={address.email}
                onChange={handleChange}
                className="name input-props"
                error={!!formErrors.email}
              />
              <ErrorTemplate error={formErrors.email} />
            </div>
            <div className="two-divider">
              <TextField
                label="Mobile Number"
                variant="outlined"
                type="text"
                name="phone"
                value={address.phone}
                onChange={handleChange}
                className="email input-props"
                error={!!formErrors.phone}
              />
              <ErrorTemplate error={formErrors.phone} />
            </div>
          </div>
          <div className="full-address">
            <TextField
              label="Address 1"
              variant="outlined"
              type="text"
              name="address1"
              value={address.address1}
              onChange={handleChange}
              className="full-address input-props"
              multiline
              error={!!formErrors.address1}
            />
            <ErrorTemplate error={formErrors.address1} />
          </div>
          <div className="full-address">
            <TextField
              label="Address 2"
              variant="outlined"
              type="text"
              name="address2"
              value={address.address2}
              onChange={handleChange}
              className="full-address input-props"
              multiline
            />
            <ErrorTemplate error={formErrors.address2} />
          </div>
          <div className="town-state">
            <div className="three-divider">
              <TextField
                label="City"
                variant="outlined"
                type="text"
                name="city"
                value={address.city}
                onChange={handleChange}
                className="town input-props"
                error={!!formErrors.city}
              />
              <ErrorTemplate error={formErrors.city} />
            </div>
            <div className="three-divider">
              <TextField
                label="State"
                variant="outlined"
                select
                name="province"
                value={address.province}
                onChange={handleChange}
                className="state input-props"
                error={!!formErrors.province}
              >
                {states.map((option) => (
                  <MenuItem key={option.value} value={option.label}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <ErrorTemplate error={formErrors.province} />
            </div>
            <div className="three-divider">
              <TextField
                label="Zip code"
                variant="outlined"
                inputProps={{
                  inputMode: "numeric"
                }}
                type="text"
                name="zip"
                value={address.zip}
                onChange={handleChange}
                className="state input-props"
                error={!!formErrors.zip}
              />
              <ErrorTemplate error={formErrors.zip} />
            </div>
          </div>

          <div className="address-type">
            {/* <div className='type-address-heading'></div> */}
            <div className="type-of-address">
              <h3 className="save-address">Save as</h3>
              <ul>
                <li
                  className={`inactive ${
                    address.label === "home" ? "activated" : ""
                  }`}
                  onClick={() => {
                    setAddressValues({ ...address, label: "home" });
                  }}
                >
                  Home
                </li>
                <li
                  className={`inactive ${
                    address.label === "office" ? "activated" : ""
                  }`}
                  onClick={() => {
                    setAddressValues({ ...address, label: "office" });
                  }}
                >
                  Office
                </li>
                <li
                  className={`inactive ${
                    address.label &&
                    address.label !== "office" &&
                    address.label !== "home"
                      ? "activated"
                      : ""
                  }`}
                >
                  <button
                    type="button"
                    className="switch-btn"
                    onClick={switchInput}
                  >
                    Others
                  </button>

                  {inputOpen && (
                    <input
                      type="text"
                      placeholder="enter a label"
                      name="label"
                      value={address.label}
                      onChange={handleChange}
                    />
                  )}
                </li>
              </ul>
            </div>
          </div>
          <FormControlLabel
            control={
              <Checkbox onClick={handleApplyGst} checked={address.gstEnabled} />
            }
            className="form-control-label"
            label={
              <Typography fontSize={14} fontWeight={600}>
                Apply GST
              </Typography>
            }
          />
          {address?.gstEnabled && (
            <div className="company-details">
              <div className="two-divider">
                <TextField
                  label="Company Name"
                  variant="outlined"
                  type="text"
                  name="companyName"
                  value={address.companyName}
                  onChange={handleChange}
                  className="full-address input-props"
                  error={!!formErrors.companyName}
                  disabled={!address.gstEnabled}
                />
                <ErrorTemplate error={formErrors.companyName} />
              </div>
              <div className="two-divider">
                <TextField
                  label="GST No."
                  variant="outlined"
                  type="text"
                  name="gstNumber"
                  value={address.gstNumber}
                  onChange={handleChange}
                  className="full-address input-props"
                  error={!!formErrors.gstNumber}
                  disabled={!address.gstEnabled}
                />
                <ErrorTemplate error={formErrors.gstNumber} />
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
